import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";
import TitleBar from "../../designSystem/c-organism/titleBar/TitleBar";
import Icon from "../../designSystem/a-atoms/icons/Icon";
import { getFeedback, getSummary } from "../../services/insightsApi";
import style from "./Insights.module.css";

const SENTIMENT_ICONS = {
  positive: "sentiment_satisfied",
  negative: "sentiment_dissatisfied",
  neutral: "sentiment_neutral",
};

const CHART_PALETTE = [
  "#6366f1", "#06b6d4", "#f59e0b", "#ef4444", "#10b981",
  "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#3b82f6",
];

const InsightsCategory = () => {
  const org = useSelector((state) => state.orgStatus.value);
  const { categoryName } = useParams();
  const decodedName = decodeURIComponent(categoryName);

  const [records, setRecords] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!org?.payload) return;
    setLoading(true);
    setError(null);

    Promise.all([
      getFeedback(org.payload, { category: decodedName, limit: 500 }),
      getSummary(org.payload),
    ])
      .then(([feedbackRes, summaryRes]) => {
        setRecords(feedbackRes.records);

        const catEntry = Object.values(summaryRes.categories || {}).find(
          (c) => c.name === decodedName
        );
        if (catEntry?.subCategories) {
          setSubCategories(
            Object.entries(catEntry.subCategories)
              .map(([id, sc]) => ({ id, name: sc.name, count: sc.count }))
              .sort((a, b) => b.count - a.count)
          );
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [org?.payload, decodedName]);

  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  const sixMonthsCutoff = sixMonthsAgo.toISOString().substring(0, 10);

  const volumeByDay = {};
  for (const r of records) {
    const day = r.date ? r.date.substring(0, 10) : null;
    if (!day || day < sixMonthsCutoff) continue;
    volumeByDay[day] = (volumeByDay[day] || 0) + 1;
  }
  const volumeData = Object.entries(volumeByDay)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, count]) => ({ day, count }));

  const filteredRecords = selectedSubCategory
    ? records.filter((r) => r.subCategory === selectedSubCategory)
    : records;

  function handleSubCategoryClick(data) {
    if (selectedSubCategory === data.name) {
      setSelectedSubCategory(null);
    } else {
      setSelectedSubCategory(data.name);
    }
  }

  return (
    <div className="mainContentInner">
      <TitleBar
        title={decodedName}
        breadcrumb={[
          { name: "Home", url: "/" },
          { name: "Insights", url: "/insights" },
        ]}
      />

      <div className="contentPadding">
        {loading && <div className={style.loadingState}>Loading...</div>}
        {error && <div className={style.resultError}><Icon>error</Icon> {error}</div>}

        {!loading && !error && (
          <>
            <div className={style.chartsGrid}>
              {volumeData.length > 1 && (
                <div className={style.chartSection}>
                  <h3>Feedback Volume Over Time</h3>
                  <div className={style.chartWrapper}>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={volumeData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--on--700)" />
                        <XAxis dataKey="day" stroke="var(--on--300)" fontSize={11} tick={{ angle: -45, textAnchor: "end" }} height={60} />
                        <YAxis stroke="var(--on--300)" fontSize={12} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "var(--on--800)",
                            border: "1px solid var(--on--600)",
                            borderRadius: "8px",
                            color: "var(--on--100)",
                          }}
                        />
                        <Line type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {subCategories.length > 0 && (
                <div className={style.chartSection}>
                  <h3>
                    Sub-categories
                    {selectedSubCategory && (
                      <button className={style.clearFilter} onClick={() => setSelectedSubCategory(null)}>
                        Clear filter: {selectedSubCategory}
                        <Icon>close</Icon>
                      </button>
                    )}
                  </h3>
                  <div className={style.chartWrapper}>
                    <ResponsiveContainer width="100%" height={Math.max(150, subCategories.length * 35)}>
                      <BarChart data={subCategories} layout="vertical" margin={{ left: 10 }}>
                        <XAxis type="number" stroke="var(--on--300)" fontSize={12} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" stroke="var(--on--300)" fontSize={12} width={150} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "var(--on--800)",
                            border: "1px solid var(--on--600)",
                            borderRadius: "8px",
                            color: "var(--on--100)",
                          }}
                        />
                        <Bar
                          dataKey="count"
                          radius={[0, 4, 4, 0]}
                          cursor="pointer"
                          onClick={handleSubCategoryClick}
                        >
                          {subCategories.map((sc, i) => (
                            <Cell
                              key={sc.id}
                              fill={CHART_PALETTE[i % CHART_PALETTE.length]}
                              opacity={selectedSubCategory && selectedSubCategory !== sc.name ? 0.3 : 1}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            <div className={style.quotesSection}>
              <h3>
                Feedback ({filteredRecords.length})
                {selectedSubCategory && <span className={style.filterLabel}> — {selectedSubCategory}</span>}
              </h3>
              <div className={style.quoteGrid}>
                {filteredRecords.map((record) => (
                  <div key={record.id} className={style.quoteCard}>
                    <div className={style.quoteContent}>
                      <q className={style.quoteText}>
                        {record.text || "No text content"}
                      </q>
                      <div className={style.quoteMeta}>
                        {record.date && (
                          <span className={style.quoteMetaItem}>
                            <Icon>calendar_today</Icon> {record.date}
                          </span>
                        )}
                        {record.subCategory && (
                          <span className={style.quoteMetaItem}>
                            <Icon>category</Icon> {record.subCategory}
                          </span>
                        )}
                        {record.sentiment && (
                          <span className={`${style.quoteMetaItem} ${style[record.sentiment]}`}>
                            <Icon>{SENTIMENT_ICONS[record.sentiment] || "help"}</Icon>
                          </span>
                        )}
                        {record.rating && (
                          <span className={style.quoteMetaItem}>
                            <Icon>star</Icon> {record.rating}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {filteredRecords.length === 0 && (
                  <div className={style.noResults}>No feedback found for this category.</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InsightsCategory;
